import { useCallback, useEffect, useId, useRef, useState } from "react";

import { themeVar as colors, radius, shadow, type as typeTokens } from "../design-tokens";
import { blockStyle } from "../ui/Block";
import { REPORT_TIERS, REPORT_TIER_HINTS, REPORT_TIER_LABELS, type ReportTier } from "../cma/constants";
import { VERTICAL_LIVE, statusWord } from "../verticalStatus";
import { BUILDER_PATH, VERTICALS } from "./verticals";

/** The tier the dialog starts on — the builder's own default. */
const DEFAULT_TIER: ReportTier = "amb";

/**
 * client/src/home/NewBriefChooser.tsx — the one "New brief" chooser, used from two screens.
 *
 * ONE COMPONENT, TWO CALL SITES: the app home at `/app` and the shelf at `/briefs`. Two copies of
 * a chooser is two places for the three verticals to fall out of step, and this product's first
 * rule about them is that they are co-equal — a rule a second copy can break silently.
 *
 * READ `verticals.ts` BEFORE CHANGING THIS. It records what was measured about how a vertical
 * reaches the builder (it does not), and why all three cards therefore carry the same action in
 * the same words rather than pretending to file a choice the wire has no field for.
 *
 * NO PRIMARY. All three cards share one style object. There is no `primary` prop, no accent fill
 * on one of them and no ordering hint — if a future change wants to emphasise one, it has to do it
 * deliberately and answer to `NewBriefChooser.test.tsx`, which asserts the three rendered cards are
 * indistinguishable from one another in class, chrome and action text.
 *
 * ACCESSIBILITY. `role="dialog"` with `aria-modal`, labelled by its own heading. Focus moves into
 * the panel on open and returns to whatever opened it on close. Escape closes. Tab cycles inside
 * the panel rather than escaping to the page behind it. Every card is a real `<a>`, so it is a tab
 * stop, it takes a focus ring, and middle-click and "open in new tab" work the way a link should.
 *
 * WHY A LINK AND NOT A ROUTER PUSH. Same reason `ui/AppShell`'s primary action reaches the builder by a plain `<a>`:
 * starting a fresh brief must drop every scrap of the previous job's in-memory state, which a hard
 * navigation guarantees and a client-side route change does not.
 */

export interface NewBriefChooserProps {
  open: boolean;
  onClose: () => void;
}

/** Everything inside the panel that can take focus, in document order. */
function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
  );
}

export function NewBriefChooser({ open, onClose }: NewBriefChooserProps) {
  const headingId = useId();
  const noteId = useId();
  const [tier, setTier] = useState<ReportTier>(DEFAULT_TIER);
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** Whatever had focus when the chooser opened. Returning focus there on close is the half of a
   *  dialog people notice only when it is missing: without it the next Tab starts at the top of
   *  the document, a long way from the button that was just pressed. */
  const openerRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const first = panel ? focusables(panel)[0] : undefined;
    // The panel itself is the fallback target — it carries `tabIndex={-1}` so a screen reader lands
    // on the heading rather than on the first link with no context read out first.
    (first ?? panel)?.focus();
    return () => {
      openerRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = focusables(panel);
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === firstItem || active === panel)) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && active === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    }
    // On the document, not on the input: a handler bound to one element goes dead the moment focus
    // moves off it, which is how a dialog ends up impossible to close with the keyboard.
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      // A scrim, not a decoration: it is what makes the panel read as over the page rather than in
      // it, and clicking it is the gesture half this product's users will try first.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "6vh 16px 24px",
        overflowY: "auto",
        background: "rgba(15, 23, 42, 0.38)",
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={noteId}
        tabIndex={-1}
        style={{
          /* THE MEASURE COMES FROM THE SYSTEM, AND IT HAD TO (measured in Chromium, 2026-09-22).
             This was a hand-picked `maxWidth: 460`, which is under `measureLiterals`' 480px floor
             and therefore legal — and wrong. At a 1440px viewport the three cards measured tops of
             185, 185, 314: TWO on the first row and one wrapped beneath them. A 2 + 1 wrap makes
             the third card the odd one out, which is the "one vertical is primary" failure arriving
             through layout rather than through styling — the thing the equality test in
             `NewBriefChooser.test.tsx` cannot see, because jsdom has no layout engine.

             `copy` is 640. Three cards at 640 − 40 padding − 20 gap is 193px each, which is a real
             column. Taking it from `ui/Block` rather than typing 640 here is the point of that
             module existing. */
          ...blockStyle("copy"),
          padding: "22px 20px 20px",
          borderRadius: radius.card,
          border: `1px solid ${colors.border}`,
          background: colors.bgSurface,
          boxShadow: shadow.subtle,
          outline: "none",
        }}
      >
        <h2
          id={headingId}
          style={{
            margin: "0 0 6px",
            fontFamily: typeTokens.fontUi,
            fontSize: typeTokens.scale.md,
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          New brief
        </h2>
        {/* THE REAL FIRST CHOICE (2026-09-25, owner direction). This dialog used to offer the three
            verticals — the axis that changes nothing, since a vertical has no field on the job —
            and hid the report tier, the axis that changes everything. The owner opened it and
            concluded the CMA and the AMB had been deleted. The tier comes first now, in the words
            `cma/constants.ts` already uses, and it travels to the builder in the address so the
            choice is honored rather than asked again. AMB stays the default, as in the builder. */}
        <p
          id={noteId}
          style={{ margin: "0 0 12px", fontSize: typeTokens.scale.dense, lineHeight: 1.5, color: colors.textSecondary }}
        >
          Choose the report. The builder opens with it selected, and you can change it there.
        </p>

        <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
          <legend style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
            Report
          </legend>
          <div role="radiogroup" aria-label="Report" style={{ display: "grid", gap: 8 }}>
            {REPORT_TIERS.map((t) => {
              const on = t === tier;
              return (
                <label
                  key={t}
                  data-vb-tier={t}
                  style={{
                    display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px",
                    borderRadius: radius.control, cursor: "pointer",
                    border: `1px solid ${on ? colors.accent : colors.border}`,
                    background: on ? colors.accentSoft : colors.bgApp,
                  }}
                >
                  <input
                    type="radio"
                    name="vb-new-brief-tier"
                    value={t}
                    checked={on}
                    onChange={() => setTier(t)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ display: "grid", gap: 2 }}>
                    <span style={{ fontSize: typeTokens.scale.base, fontWeight: 600, color: colors.textPrimary }}>
                      {REPORT_TIER_LABELS[t]}
                    </span>
                    <span style={{ fontSize: typeTokens.scale.sm, lineHeight: 1.45, color: colors.textSecondary }}>
                      {REPORT_TIER_HINTS[t]}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {/* WHO READS IT — CONTEXT, NOT A GATE. The verticals stay visible and co-equal (same chrome,
            no primary styling, no link) because the brief serves all three. They are not a
            choice: a vertical has no field on the job, so a card that looked selectable would imply
            something the server cannot store. Each says whether it is live, in the marketing page's
            own words, read from the same source (`verticalStatus.ts`). */}
        <h3 style={{ margin: "16px 0 6px", fontSize: typeTokens.scale.dense, fontWeight: 600, color: colors.textPrimary }}>
          Who reads it
        </h3>
        <p style={{ margin: "0 0 8px", fontSize: typeTokens.scale.sm, lineHeight: 1.45, color: colors.textSecondary }}>
          The same brief serves all three. This is for context — it is not saved with the brief.
        </p>
        <ul className="vb-chooser-grid">
          {VERTICALS.map((vertical) => {
            const live = VERTICAL_LIVE[vertical.id];
            return (
              <li
                key={vertical.id}
                data-vb-vertical={vertical.id}
                style={{
                  display: "flex", flexDirection: "column", gap: 4, padding: "10px 12px",
                  borderRadius: radius.control, border: `1px solid ${colors.border}`,
                  background: colors.bgApp, color: colors.textPrimary,
                }}
              >
                <span style={{ fontSize: typeTokens.scale.dense, fontWeight: 600 }}>{vertical.name}</span>
                <span style={{ fontSize: typeTokens.scale.xs, lineHeight: 1.45, color: colors.textSecondary }}>
                  {vertical.audience}
                </span>
                <span data-vb-live={live ? "yes" : "no"} style={{ marginTop: "auto", paddingTop: 4, fontSize: typeTokens.scale.xs, color: colors.textFaint }}>
                  {statusWord(live)}
                </span>
              </li>
            );
          })}
        </ul>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={close}
            style={{
              padding: "8px 14px",
              borderRadius: radius.control,
              border: `1px solid ${colors.border}`,
              background: colors.bgSurface,
              color: colors.textPrimary,
              fontSize: typeTokens.scale.dense,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          {/* THE ONE PRIMARY ACTION. A plain link, not a router push: a new brief must start from a
              clean page with none of the previous job's state, which a hard navigation guarantees. */}
          <a href={`${BUILDER_PATH}?tier=${tier}`} className="btn p" data-testid="new-brief-start"
             style={{ textDecoration: "none" }}>
            Start brief
          </a>
        </div>
      </div>
    </div>
  );
}
